<template>
    <div
        class="col-span-12 lg:col-span-6 flex flex-col gap-4 bg-white dark:bg-slate-800 rounded-lg shadow dark:shadow-slate-900/50 overflow-hidden p-6 pb-8 md:min-h-[400px] relative"
    >
        <!-- Header -->
        <div
            class="flex items-center justify-between mb-6"
            v-if="walletCardCount > 0"
        >
            <div class="flex items-center gap-2">
                <Icon
                    icon="coin"
                    width="24"
                    height="24"
                    class="text-slate-800 dark:text-slate-100"
                />
                <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100">
                    My benefits - {{ month }} {{ year }}
                </h3>
            </div>

            <!-- View Cards Link -->
            <Link :href="`/card-benefits`" class="link-base link-dark">
                View benefits
                <icon icon="arrow" />
            </Link>
        </div>

        <!-- NO cards in wallet state-->
        <div
            v-if="!loading && walletCardCount === 0"
            class="w-full flex flex-col items-center pt-4 justify-center"
        >
            <div
                class="w-full flex flex-col text-center items-center justify-center pb-6"
            >
                <img
                    src="../../../../images/monthly-benefits-state-promo.png"
                    alt="Monthly benefits"
                />
                <p class="mb-4">
                    <span class="font-bold text-slate-800 dark:text-slate-100 text-3xl">
                        Get the most from
                    </span>
                    <br />
                    <span class="font-bold text-orange text-3xl">
                        your benefits
                    </span>
                </p>
                <p class="text-slate-500 dark:text-slate-400 text-lg px-6 mb-6">
                    Keep an eye on your benefits usage to make the
                    <br />
                    most of your monthly balance.
                </p>
                <Link class="btn btn-orange" href="/card-benefits">
                    View my benefits
                </Link>
            </div>
        </div>

        <!-- Progress bar and amount - positioned halfway down -->
        <div
            v-if="!loading && walletCardCount > 0"
            class="flex-1 flex flex-col justify-center"
        >
            <div class="flex items-end gap-2 justify-between">
                <div>
                <p class="text-4xl font-semibold text-slate-800 dark:text-slate-100">
                        {{
                            Filters.formatSmartMoney(
                                monthlyBenefits.used_this_month
                            )
                        }}
                    </p>
                <p class="text-sm text-slate-500 dark:text-slate-400">Used this month</p>
                </div>
                <div class="w-30 flex flex-col justify-end items-end">
                    <div class="flex items-center relative w-30 mb-1">
                        <img
                            v-for="(
                                card, index
                            ) in monthlyBenefits?.top_3_cards"
                            :key="card.id"
                            :src="card.image_url"
                            alt="Best offer card"
                            class="relative w-14 h-9 rounded-sm"
                            :style="`margin-left: ${index > 0 ? -26 : 0}px`"
                        />
                        <img
                            v-if="monthlyBenefits?.top_3_cards?.length === 0"
                            src="../../../../images/no-local-card.png"
                            class="ml-auto"
                            alt="No cards with benefits"
                        />
                    </div>
                    <p
                    class="text-sm text-slate-500 dark:text-slate-400"
                        v-if="monthlyBenefits?.card_count > 0"
                    >
                        {{ monthlyBenefits.card_count }}
                        {{
                            monthlyBenefits.card_count === 1 ? 'card' : 'cards'
                        }}
                        tracked
                    </p>
                <p class="text-sm text-slate-500 dark:text-slate-400" v-else>
                        No trackable monthly benefits
                    </p>
                </div>
            </div>
            <div class="mt-4">
                <div
                    class="w-full h-2 bg-orange-200 rounded-md relative"
                    :class="
                        monthlyBenefits.utilization > 0
                        ? 'bg-orange-200 dark:bg-orange-900/50'
                        : 'bg-slate-200 dark:bg-slate-700'
                    "
                >
                    <div
                        v-if="monthlyBenefits.utilization > 0"
                        class="h-2 bg-orange-600 rounded-md"
                        :style="`width: ${monthlyBenefits.utilization}%`"
                    ></div>
                    <div
                        v-if="monthlyBenefits.utilization > 0"
                        class="absolute -top-2.5"
                        :style="`width: 28px; height: 28px; left: calc(${monthlyBenefits.utilization}% - 14px)`"
                    >
                        <svg
                            width="28"
                            height="28"
                            viewBox="0 0 28 28"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <rect
                                width="28"
                                height="28"
                                rx="14"
                                fill="#FD421D"
                            />
                            <path
                                d="M14.6133 6.17505C14.5572 5.88026 14.2994 5.66699 13.9993 5.66699C13.6993 5.66699 13.4415 5.88026 13.3854 6.17505C12.9864 8.26994 12.1991 9.86461 11.0314 11.0323C9.86364 12.2001 8.26896 12.9873 6.17407 13.3864C5.87929 13.4425 5.66602 13.7002 5.66602 14.0003C5.66602 14.3004 5.87929 14.5581 6.17407 14.6143C8.26896 15.0133 9.86364 15.8006 11.0314 16.9683C12.1991 18.136 12.9864 19.7307 13.3854 21.8256C13.4415 22.1204 13.6993 22.3337 13.9993 22.3337C14.2994 22.3337 14.5572 22.1204 14.6133 21.8256C15.0123 19.7307 15.7996 18.136 16.9673 16.9683C18.1351 15.8006 19.7297 15.0133 21.8246 14.6143C22.1194 14.5581 22.3327 14.3004 22.3327 14.0003C22.3327 13.7002 22.1194 13.4425 21.8246 13.3864C19.7297 12.9873 18.1351 12.2001 16.9673 11.0323C15.7996 9.86461 15.0123 8.26994 14.6133 6.17505Z"
                                fill="white"
                            />
                        </svg>
                    </div>
                </div>
            </div>
        </div>

        <!-- Bottom stats - positioned at bottom -->
        <div class="flex items-center gap-2 justify-between mt-auto">
            <div>
                <p class="text-2xl font-semibold text-slate-800 dark:text-slate-100">
                    {{
                        Filters.formatSmartMoney(
                            monthlyBenefits.total_value_up_to
                        )
                    }}
                </p>
                <p class="text-sm text-slate-500 dark:text-slate-400">Total value up to</p>
            </div>
            <div>
                <p class="text-2xl font-semibold text-slate-800 dark:text-slate-100">
                    {{ Filters.formatSmartMoney(monthlyBenefits.left_to_use) }}
                </p>
                <p class="text-sm text-slate-500 dark:text-slate-400">Left to use</p>
            </div>
            <div>
                <p class="text-2xl font-semibold text-slate-800 dark:text-slate-100">
                    {{ monthlyBenefits.utilization || 0 }}%
                </p>
                <p class="text-sm text-slate-500 dark:text-slate-400">Utilization</p>
            </div>
        </div>
    </div>
</template>
<script setup>
import { Link } from '@inertiajs/vue3';
import { ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useDashboardStore } from '@/store/dashboard';
import Icon from '@/components/ui/icon/icon.vue';
import dayjs from 'dayjs';
import Filters from '@/filters';

const month = ref(dayjs().format('MMMM'));
const year = ref(dayjs().format('YYYY'));
const dashboardStore = useDashboardStore();
const { monthlyBenefits, loading, walletCardCount } =
    storeToRefs(dashboardStore);
</script>
